import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'
import Swiper, { A11y, Navigation } from 'swiper'

Swiper.use([A11y, Navigation])

/**
 * Controller for the Card Flip Gallery
 */
class CardFlipGallery {
  static mobileQuery = window.matchMedia(`(max-width: ${GLOBAL_CONSTANTS.BREAKPOINTS.mobile}px)`)
  static touchQuery = window.matchMedia('(pointer: coarse)')

  constructor(element) {
    this.element = element

    this.carousel = null
    this.swiperNavNext = this.element.querySelector('.js-card-flip-nav-next')
    this.swiperNavPrev = this.element.querySelector('.js-card-flip-nav-prev')
    this.list = this.element.querySelector('.js-card-flip-list')
    this.description = this.element.querySelector('.js-card-flip-description')
    this.cardPairs = [...this.element.querySelectorAll('.js-card-flip-gallery-item')]
      .map(item => new CardFlipGalleryCardPair(item))

    CardFlipGallery.mobileQuery.addEventListener('change', () => {
      this.toggleCarousel()
    })

    this.toggleCarousel()

    // Helps prevent the flip animation from playing on page load
    setTimeout(() => {
      this.element.removeAttribute('data-preload')
    }, 750)
  }

  /**
   * Creates or destroys the Swiper instance depending on mobile/desktop layout
   */
  toggleCarousel = () => {
    const isMobile = CardFlipGallery.mobileQuery.matches

    if (isMobile) {
      this.description.ariaHidden = true
      this.createCarousel()
      window.addEventListener('resize', this.updateSlidesPerView)
    } else {
      this.description.ariaHidden = false
      this.carousel?.destroy(true, true)
      this.cardPairs.forEach(pair => {
        // Reset settings added by Swiper
        pair.element.role = pair.element.dataset.role
        pair.element.ariaLabel = pair.element.dataset.label
      })
      this.list.ariaLive = 'off'
      window.removeEventListener('resize', this.updateSlidesPerView)
    }
  }

  /**
   * Updates Swiper instance params with calculated width and gap
   */
  updateSlidesPerView = () => {
    const {
      slidesPerView,
      spaceBetween
    } = this.getSlideCalculations()

    this.carousel.params.slidesPerView = slidesPerView
    this.carousel.params.spaceBetween = spaceBetween
    this.carousel.update()
  }

  /**
   * Calculates values needed by Swiper to size and align slides
   */
  getSlideCalculations = () => {
    const main = document.querySelector('#site-main')

    const {
      paddingLeft,
      paddingRight
    } = getComputedStyle(main)

    const elementWidth = main.clientWidth
    const carouselWidth = elementWidth - parseFloat(paddingLeft) - parseFloat(paddingRight)

    return {
      slidesPerView: elementWidth / carouselWidth,
      spaceBetween: parseFloat(paddingLeft) / 2
    }
  }

  /**
   * Creates a Swiper instance from the gallery
   */
  createCarousel = () => {
    const {
      slidesPerView,
      spaceBetween
    } = this.getSlideCalculations()

    this.carousel = new Swiper(this.element, {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
      centeredSlides: true,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1
      },
      freeMode: {
        enabled: true,
        sticky: true,
        momentumBounce: false
      },
      navigation: {
        nextEl: this.swiperNavNext,
        prevEl: this.swiperNavPrev
      },
      a11y: {
        enabled: true,
        slideLabelMessage: 'Card pair {{index}} of {{slidesLength}}',
        prevSlideMessage: 'Previous pair of cards',
        nextSlideMessage: 'Next pair of cards'
      }
    })
  }
}

/**
 * Controller for each individual Card Flip Gallery card pair
 */
class CardFlipGalleryCardPair {
  constructor(element) {
    this.element = element
    this.button = this.element.querySelector('.js-card-flip-gallery-button')
    this.link = this.element.querySelector('.js-card-flip-link') || document.createElement('a')
    this.front = this.element.querySelector('.js-card-front')
    this.back = this.element.querySelector('.js-card-back')

    CardFlipGallery.mobileQuery.addEventListener('change', () => {
      this.updateLayout()
    })

    CardFlipGallery.touchQuery.addEventListener('change', () => {
      this.updateLayout()
    })

    this.updateLayout()
  }

  /**
   * Swaps card layouts based desktop or mobile
   */
  updateLayout = () => {
    const isMobile = CardFlipGallery.mobileQuery.matches

    this.button.ariaPressed = 'false'

    if (isMobile) {
      this.setupMobile()
    } else {
      this.setupDesktop()
    }
  }

  /**
   * Prepares the card pair for desktop layout (a11y attrs and event listeners)
   */
  setupDesktop = () => {
    this.front.ariaHidden = false
    this.back.ariaHidden = true

    this.link.ariaHidden = true
    this.link.tabIndex = '-1'

    this.button.ariaHidden = false
    this.button.disabled = false
    this.button.tabIndex = '0'

    this.element.addEventListener('focusout', this.showFront, true)

    if (CardFlipGallery.touchQuery.matches) {
      this.element.addEventListener('touchend', this.flipCard, true)
      this.button.addEventListener('keydown', this.flipCard, true)
    } else {
      this.button.addEventListener('click', this.flipCard, true)
      this.element.addEventListener('mouseenter', this.showBack, true)
      this.element.addEventListener('mouseleave', this.showFront, true)
    }
  }

  /**
   * Prepares the card pair for mobile layout (a11y attrs and event listeners)
   */
  setupMobile = () => {
    this.front.ariaHidden = false
    this.back.ariaHidden = false

    this.link.ariaHidden = false
    this.link.tabIndex = '0'

    this.button.ariaHidden = true
    this.button.disabled = true
    this.button.tabIndex = '-1'

    this.element.removeEventListener('focusout', this.showFront, true)
    this.element.removeEventListener('touchend', this.flipCard, true)
    this.button.removeEventListener('keydown', this.flipCard, true)
    this.button.removeEventListener('click', this.flipCard, true)
    this.element.removeEventListener('mouseenter', this.showBack, true)
    this.element.removeEventListener('mouseleave', this.showFront, true)
  }

  /**
   * Show the front state of the card pair
   */
  showFront = (event) => {
    if (
      event &&
      ['focusout', 'mouseleave'].includes(event.type) &&
      this.element.contains(event.relatedTarget)
    ) {
      return
    }

    this.front.ariaHidden = false
    this.back.ariaHidden = true

    this.link.ariaHidden = true
    this.link.tabIndex = '-1'

    this.button.ariaPressed = 'false'
    this.button.ariaLabel = 'Flip to back card'
  }

  /**
   * Show the back state of the card pair
   */
  showBack = () => {
    this.front.ariaHidden = true
    this.back.ariaHidden = false

    this.link.ariaHidden = false
    this.link.tabIndex = '0'

    this.button.ariaPressed = 'true'
    this.button.ariaLabel = 'Flip to front card'
  }

  /**
   * Toggle the card's front/back state
   * @param {Event} e - The event that fired this action
   */
  flipCard = (event) => {
    // Don't flip if the keydown event is for any key other than Enter and Space
    if (event?.type === 'keydown' && event.code !== 'Enter' && event.code !== 'Space') {
      return
    }

    // Don't flip if the touchend was triggered by a link
    if (event?.type === 'touchend' && event.target.nodeName === 'A') {
      return
    }

    event.preventDefault()
    if (this.button.ariaPressed === 'false') {
      this.showBack()
    } else {
      this.showFront()
    }
  }
}

export default {
  selector: '.js-card-flip-gallery',
  Source: CardFlipGallery
}
